import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { WaitingCount } from "@dentacore/shared-types";

import { readWaitingCount } from "../api/booking-requests";
import { useActiveBranch } from "../api/use-active-branch";
import { useCan } from "../auth/use-can";
import { useModules } from "./modules-context";

interface Waiting {
  /** Null while it is being read, and when the read failed. */
  count: WaitingCount | null;
  /** Re-read now. Call after validating, approving or declining. */
  refresh: () => void;
}

const WaitingContext = createContext<Waiting>({ count: null, refresh: () => undefined });

/**
 * How many things are waiting on a human, shared by the header chip and the
 * queue beneath it (STORY-057).
 *
 * ── This is what "live" means here, and it is bounded on purpose ────────────
 *
 * P3 Q9 deferred live updates to Phase 10 because **how they should be built
 * depends on the hosting decision nobody has made**, and an in-memory gateway
 * breaks silently as a stale screen the day a second API process exists. So
 * decision HC2 scopes the story's word *live* to what needs no transport:
 *
 *   - **your own actions** — validating, approving or declining calls
 *     `refresh`, and the number drops before you look up at it;
 *   - **coming back to the tab** — a `visibilitychange` listener, because a
 *     receptionist who has been away is the reader most likely to be looking at
 *     something stale.
 *
 * **There is no timer, and adding one is a later decision rather than a
 * missing line.** This is the first shared-state context in the app that could
 * have had one, and a 30-second poll here would be a partial answer to the
 * question P3 Q9 parked — and a standing request per open tab for a difference
 * a single reader would rarely notice.
 *
 * ── A failed read shows nothing rather than a stale number ──────────────────
 *
 * `count` goes back to null, so the chip disappears. Keeping the last value is
 * the tempting alternative and it is worse: a header confidently reporting
 * "3 waiting" that has been wrong for an hour is how somebody stops trusting
 * the chip entirely. Silence is honest; a wrong number is not. Nothing is put
 * on screen either — this is ambient chrome, and an error banner over a count
 * that failed to load is not what a dentist needs to be told about.
 */
export function WaitingProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const branchId = useActiveBranch();
  const modules = useModules();
  // The queue is the Chatbot's (MOD-D16): a clinic without it has nothing waiting,
  // and asking would only be refused (STORY-188b).
  // Only once the modules are known, so nothing is asked and then abandoned.
  // And only for someone who can work the queue: the chip leads to it, and
  // calendar.view alone opened an empty panel.
  const can = useCan();
  const hasChatbot =
    modules.states !== null && modules.has("CHATBOT") && can("appointments.validate");
  const [count, setCount] = useState<WaitingCount | null>(null);
  // Bumped to ask for a re-read. A nonce rather than a stored promise, so
  // overlapping refreshes collapse into the effect's own cleanup.
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((was) => was + 1), []);

  useEffect(() => {
    // Null is "not yet", not "none" — see `useActiveBranch`. Reading with no
    // branch header is refused, and a refusal here would blank the chip.
    if (branchId === null || !hasChatbot) {
      setCount(null);
      return;
    }

    const controller = new AbortController();
    readWaitingCount(controller.signal)
      .then(setCount)
      .catch(() => {
        if (controller.signal.aborted) return;
        setCount(null);
      });

    return () => controller.abort();
  }, [branchId, hasChatbot, nonce]);

  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState === "visible") refresh();
    };

    document.addEventListener("visibilitychange", onVisible);
    // Both, because a window focused without ever having been hidden — moving
    // back from another window on the same screen — fires only the second.
    window.addEventListener("focus", refresh);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, [refresh]);

  return <WaitingContext.Provider value={{ count, refresh }}>{children}</WaitingContext.Provider>;
}

/**
 * The count, and the way to ask for a fresh one.
 *
 * **`refresh` is safe to call from anywhere inside the shell**, including from
 * a screen with no chip on it — the default context is a no-op, so a component
 * rendered outside the provider in a test does not have to know.
 */
export function useWaiting(): Waiting {
  return useContext(WaitingContext);
}
